import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';

import Lock from '@pkg/assets/images/Lock.png';
import WavingHand from '@pkg/assets/images/Waving_Hand.png';
import { DepButton, DepButtonDisplay, DepInput, DepLinkButton } from '@pkg/components/deprecated';
import { useAppDispatch } from '@pkg/hooks';

import { type ApiResult, type MagicLinkResponseValue } from '@app/client';
import { validEmail } from '@app/helpers';
import { PathParams } from '@app/routes/types';
import { login, magicLink } from '@app/store/actions/auth';
import { addNotification } from '@app/store/actions/notification';

import { Typography } from '../DesignSystem';
import YatPasswordInput from '../Input/PasswordInput';

import './_LoginForm.scss';

enum Steps {
    Email,
    Password,
}

interface YatLoginFormProps {
    buttonCopy?: string;
    partnerKey?: string;
    isPartnerCreateAccount?: boolean;
}

const YatLoginForm = ({ buttonCopy, partnerKey, isPartnerCreateAccount }: YatLoginFormProps) => {
    const { t } = useTranslation(['account', 'translation', 'common', 'partner_integrations']);
    const { [PathParams.Yat]: yat } = useParams();

    const { state: locationState } = useLocation();

    const isAdminSignIn = Boolean(useMatch({ path: '/admin/sign-in' }));
    const [showPasswordField, setShowPasswordField] = useState(isAdminSignIn);
    const [emailIsInvalid, setEmailIsInvalid] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [password, setPassword] = useState('');
    const [step, setStep] = useState<Steps>(Steps.Email);

    const [email, setEmail] = useState('');
    const [partnerManageRedirect, setPartnerManageRedirect] = useState<string>();
    const [walletKey, setWalletKey] = useState<string>();

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    useEffect(() => {
        const stateKey = locationState?.partnerKey;
        setWalletKey(partnerKey || stateKey);
    }, [locationState, partnerKey]);

    useEffect(() => {
        if (walletKey && !isPartnerCreateAccount) {
            const redirect = yat ? `/partner/${walletKey}/manage?eid=${yat}` : `/partner/${walletKey}/manage`;
            setPartnerManageRedirect(redirect);
        }
    }, [yat, walletKey, isPartnerCreateAccount]);

    const sendMagicLink = useCallback(() => {
        if (validEmail(email)) {
            setEmailIsInvalid(false);
            const dispatchedPromise = dispatch(magicLink(email.trim(), partnerManageRedirect) as unknown) as Promise<
                ApiResult<MagicLinkResponseValue>
            >;
            dispatchedPromise
                .then((r) => {
                    if (r.ok && r.value) {
                        const newUser = r.value.status === 'RegisteredActive';
                        navigate('/magic-link-success', { state: { newUser: newUser, userEmail: email } });
                    }
                })
                .catch((e) => {
                    setIsLoggingIn(false);
                    dispatch(
                        addNotification({
                            message: e.error,
                            severity: 'error',
                        }),
                    );
                });
            return dispatchedPromise;
        }
        setEmailIsInvalid(true);
        dispatch(
            addNotification({
                message: t('valid_email_request'),
                severity: 'alert',
            }),
        );
    }, [email, dispatch, navigate, t, partnerManageRedirect]);

    const handleEmailStepSubmit = useCallback(async () => {
        setIsLoggingIn(true);
        try {
            const magicLinkResponse = await sendMagicLink();
            // No "else" because they'll be redirected if this is not the case
            if (!magicLinkResponse?.ok && (magicLinkResponse?.error as string) === 'User must use password login') {
                setStep(Steps.Password);
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (magicLinkError: any) {
            dispatch(addNotification({ message: magicLinkError.error, severity: 'error' }));
        }
    }, [dispatch, sendMagicLink]);

    const handleLogin = useCallback(
        (e?) => {
            e?.preventDefault();
            setIsLoggingIn(true);
            dispatch(login(email, password)).then((r) => {
                if (r) {
                    setIsLoggingIn(false);
                }
            });
        },
        [dispatch, email, password],
    );

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            if (showPasswordField) {
                handleLogin();
            } else {
                handleEmailStepSubmit().then(() => {
                    setIsLoggingIn(false);
                });
            }
        },
        [handleEmailStepSubmit, handleLogin, showPasswordField],
    );

    const handleChange = (e, field) => {
        const { value } = e.target;
        switch (field) {
            case 'email':
                setEmailIsInvalid(false);
                setEmail(value);
                break;
            case 'password':
                setPassword(value);
                break;
        }
    };

    const handleShowPassword = useCallback(
        (e): void => {
            if (e.shiftKey) {
                setShowPasswordField(!showPasswordField);
            }
        },
        [showPasswordField],
    );

    if (step === Steps.Password) {
        return (
            <div className="login-form user-account-page__content">
                {!walletKey && !isPartnerCreateAccount && (
                    <>
                        <img className="user-account-page__img" src={Lock} alt={t('translation:yat_smileys')} />
                        <h1 className="v2-text-heading-2">{t('account:enter_password_title')}</h1>
                        <p
                            className="user-account-page__copy user-account-page__copy--with-input v2-text-fluid-md"
                            dangerouslySetInnerHTML={{
                                __html: t('account:enter_password_copy', { user_email: email }),
                            }}
                        />
                    </>
                )}
                {walletKey && !isPartnerCreateAccount && (
                    <Typography type={'paragraph'} size={16} classes={['yat-connect-page__copy']} theme={'dark'}>
                        {t('partner_integrations:link_email_pw_copy', { email: email })}
                    </Typography>
                )}
                <form className={'login-form__form'} onSubmit={handleLogin}>
                    <YatPasswordInput
                        classes="user-account-page__input"
                        autoComplete="current-password"
                        isInvalid={false}
                        onChange={(e) => handleChange(e, 'password')}
                        value={password}
                        autoFocus={true}
                    />
                    <div className="user-account-page__btn">
                        <DepButton
                            display={DepButtonDisplay.minWidth}
                            variant="large"
                            type={'submit'}
                            loading={isLoggingIn}
                        >
                            {buttonCopy ?? t('common:lets_do_this')}
                        </DepButton>
                        <DepLinkButton to={`/forgot-password/${encodeURIComponent(email)}`} variant="invisible">
                            {t('account:forgot_password')}
                        </DepLinkButton>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div className="login-form user-account-page__content">
            {!walletKey && !isPartnerCreateAccount && (
                <>
                    <img className="user-account-page__img" src={WavingHand} alt={t('translation:yat_smileys')} />
                    <h1 className="v2-text-heading-2">{t('login')}</h1>
                    <p
                        className="user-account-page__copy user-account-page__copy--with-input v2-text-fluid-md"
                        dangerouslySetInnerHTML={{
                            __html: t('login_page_copy'),
                        }}
                    />
                </>
            )}
            {walletKey && !isPartnerCreateAccount && (
                <Typography type={'paragraph'} size={16} classes={['yat-connect-page__copy']} theme={'dark'}>
                    {t('partner_integrations:link_email_copy', { yat: t('translation:yat') })}
                </Typography>
            )}
            <form className={'login-form__form'} onSubmit={handleSubmit} noValidate>
                <DepInput
                    autoComplete="email"
                    onClick={handleShowPassword}
                    label={t('common:email')}
                    type="email"
                    onChange={(e) => handleChange(e, 'email')}
                    isInvalid={emailIsInvalid}
                    value={email}
                    errorLabel={emailIsInvalid ? t('common:error_invalid_email') : ''}
                    classes={
                        showPasswordField
                            ? 'user-account-page__input login-form__input--password-showing'
                            : 'user-account-page__input'
                    }
                    required
                />
                {showPasswordField && (
                    <div className="login-form__input--password">
                        <DepInput
                            autoComplete="current-password"
                            label={t('common:password')}
                            type="password"
                            onChange={(e) => handleChange(e, 'password')}
                            classes={'user-account-page__input'}
                            isInvalid={false}
                            value={password}
                            autoFocus={false}
                        />
                    </div>
                )}
                <div className="user-account-page__btn">
                    <DepButton
                        display={DepButtonDisplay.minWidth}
                        loading={isLoggingIn}
                        variant="large"
                        type={'submit'}
                    >
                        {buttonCopy ?? t('common:next')}
                    </DepButton>
                </div>
            </form>
        </div>
    );
};

export default YatLoginForm;
